import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { T } from './theme';

const API = 'https://tcio-api.onrender.com';
const card = { background:T.surface, border:'1px solid '+T.b200, borderRadius:8,
  boxShadow:'0 1px 2px rgba(20,45,70,0.04)' };
const label = { fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase', color:T.inkSoft, fontWeight:600 };
const inp = { font:'inherit', fontSize:13, padding:'7px 9px', borderRadius:8,
  border:'1px solid '+T.b300, color:T.ink, background:'#fff' };
const btn = (primary) => ({ font:'inherit', fontSize:12.5, fontWeight:600, padding:'6px 12px',
  borderRadius:8, cursor:'pointer', border:'1px solid '+(primary ? '#0878E8' : T.b300),
  background: primary ? '#0878E8' : '#fff', color: primary ? '#fff' : T.ink });

const DOW = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const dOnly = (v) => String(v || '').slice(0, 10);  // tolerate date OR ISO timestamp from PG
const addDays = (dateStr, n) => {
  const d = new Date(dOnly(dateStr) + 'T00:00:00'); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmtDate = (ds) => new Date(dOnly(ds) + 'T00:00:00').toLocaleDateString('en-US', { month:'numeric', day:'numeric' });
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  const ap = h >= 12 ? 'p' : 'a';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return hh + (m ? ':' + String(m).padStart(2, '0') : '') + ap;
};

export default function Scheduler({ user }) {
  const [kind, setKind] = useState('bht');
  const [grp, setGrp] = useState(null); // the facility's EMR group schedule for this week (read-only here)
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState('');
  const [monday, setMonday] = useState(() => {
    const d = new Date(); const dow = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - dow); return d.toISOString().slice(0, 10);
  });
  useEffect(() => { (async () => {
    try {
      const r = await fetch(API + '/api/facilities', { headers: { Authorization: 'Bearer ' + localStorage.getItem('tcio_token') } });
      const j = await r.json();
      if (j.ok) { setFacilities(j.data); setFacilityId(prev => prev || ((j.data[0] || {}).id || '')); }
    } catch (e) {}
  })(); }, []);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [editCell, setEditCell] = useState(null); // {templateId?, houseId, date, assignment?}
  const canEdit = ['ceo','admin','org_admin','facility_owner','operations','program_manager'].includes((user || {}).role);
  const [view, setView] = useState('house');
  const [coverFor, setCoverFor] = useState(null);
  const [coverOpts, setCoverOpts] = useState(null);
  const [coverErr, setCoverErr] = useState('');
  const [manageOpen, setManageOpen] = useState(false);
  const [fixesOpen, setFixesOpen] = useState(false);
  const [fixRows, setFixRows] = useState([]);
  const [fixActBusy, setFixActBusy] = useState(null);
  const loadFixes = useCallback(async () => {
    if (!facilityId) return;
    try {
      const r = await fetch(API + '/api/scheduler/corrections?facility_id=' + facilityId,
        { headers: { Authorization: 'Bearer ' + localStorage.getItem('tcio_token') } });
      const j = await r.json();
      if (j.ok) setFixRows(j.corrections || []);
    } catch (e) {}
  }, [facilityId]);
  useEffect(() => { if (canEdit && facilityId) loadFixes(); }, [canEdit, facilityId, loadFixes]);
  const resolveFix = async (id, approve) => {
    setFixActBusy(id);
    try {
      const r = await fetch(API + '/api/scheduler/corrections/' + id + '/resolve', {
        method: 'POST', headers: { Authorization: 'Bearer ' + localStorage.getItem('tcio_token'), 'Content-Type': 'application/json' },
        body: JSON.stringify({ approve }) });
      const j = await r.json();
      if (j.ok) { await loadFixes(); load(); }
      else alert(j.error || 'resolve failed');
    } catch (e) { alert('network error'); }
    setFixActBusy(null);
  };
  const [manageRows, setManageRows] = useState(null);
  const [manageErr, setManageErr] = useState('');
  const loadManage = async () => {
    setManageErr('');
    try {
      const r = await axios.get(API + '/api/scheduler/manage-staff', { params: { facility_id: facilityId } });
      setManageRows(r.data.staff || []);
    } catch (e) { setManageErr((e.response && e.response.data && e.response.data.error) || 'Could not load staff'); }
  };
  const toggleSchedulable = async (row) => {
    setManageErr('');
    try {
      await axios.post(API + '/api/scheduler/manage-staff/toggle',
        { facility_id: facilityId, user_id: row.user_id, schedulable: !row.schedulable });
      await loadManage();
    } catch (e) { setManageErr((e.response && e.response.data && e.response.data.error) || 'Could not update'); }
  };

  const removeAsg = async (a) => {
    if (!window.confirm('Remove ' + (a.staff_name || 'this person') + ' from this shift? It comes off the schedule (the record is kept).')) return;
    try { await axios.post(API + '/api/scheduler/assignments/' + a.id + '/remove', {}); await load(); }
    catch (e) { window.alert((e.response && e.response.data && e.response.data.error) || 'Could not remove'); }
  };
  const xBtn = (a) => canEdit ? <span title="Remove from this shift" onClick={(e) => { e.stopPropagation(); removeAsg(a); }}
    style={{ float:'right', marginLeft:6, padding:'0 4px', color:'#8291A1', fontWeight:700, cursor:'pointer', textDecoration:'none' }}>{'\u00d7'}</span> : null;
  const load = useCallback(async () => {
    if (!facilityId) return;
    setErr('');
    try {
      const r = await axios.get(API + '/api/scheduler/week',
        { params: { facility_id: facilityId, kind, week_of: monday } });
      setData(r.data);
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || e.message); }
  }, [facilityId, kind, monday]);
  useEffect(() => { load(); }, [load]);
  const loadGroups = useCallback(async () => {
    if (!facilityId) return;
    try {
      const r = await axios.get(API + '/api/scheduler/groups', { params: { facility_id: facilityId, week_of: monday } });
      setGrp(r.data && r.data.ok ? r.data : null);
    } catch (e) { setGrp(null); }
  }, [facilityId, monday]);
  useEffect(() => { loadGroups(); }, [loadGroups]);
  const toggleGroups = async () => {
    if (!grp) return;
    const on = !grp.enabled;
    if (!window.confirm(on ? 'Show the group schedule to staff on the Scheduler for this facility?' : 'Hide the group schedule from staff on the Scheduler for this facility?')) return;
    try { await axios.post(API + '/api/scheduler/groups-visibility', { facility_id: facilityId, on }); await loadGroups(); }
    catch (e) { alert((e.response && e.response.data && e.response.data.error) || 'Could not change it.'); }
  };

  const save = async (payload, existingId) => {
    setBusy(true); setErr('');
    try {
      if (existingId) await axios.patch(API + '/api/scheduler/assignments/' + existingId, payload);
      else await axios.post(API + '/api/scheduler/assignments', payload);
      setEditCell(null); await load();
    } catch (e) {
      const d = e.response && e.response.data;
      if (e.response && e.response.status === 409 && d && d.conflict) {
        setEditCell(prev => ({ ...(prev || {}), conflict: { ...d.conflict, payload, existing_id: existingId || null } }));
        setBusy(false);
        return;
      }
      const msg = (e.response && e.response.data && e.response.data.error) || e.message;
      alert('Could not save: ' + msg);
    }
    setBusy(false);
  };
  const resolveConflict = async (mode) => {
    const c = editCell && editCell.conflict;
    if (!c) return;
    setBusy(true);
    try {
      const r = await axios.post(API + '/api/scheduler/resolve-conflict',
        { mode, payload: c.payload, existing_id: c.existing_id, conflict_id: c.assignment_id });
      setEditCell(null); await load();
      if (r.data && r.data.message) alert(r.data.message);
    } catch (e) {
      alert('Could not save: ' + ((e.response && e.response.data && e.response.data.error) || e.message));
    }
    setBusy(false);
  };

  const copyWeek = async () => {
    if (!window.confirm('Copy last week\u2019s ' + kind.toUpperCase() + ' schedule into this week?')) return;
    setBusy(true);
    try {
      const r = await axios.post(API + '/api/scheduler/copy-week',
        { facility_id: facilityId, kind, from_monday: addDays(monday, -7), to_monday: monday });
      alert('Copied ' + r.data.copied + ' shifts.');
      await load();
    } catch (e) { alert('Copy failed: ' + ((e.response && e.response.data && e.response.data.error) || e.message)); }
    setBusy(false);
  };

  const clearWeek = async () => {
    if (!window.confirm('Clear the whole ' + kind.toUpperCase() + ' schedule for the week of ' + fmtDate(monday) + '?\n\nEvery shift on this grid comes off. Shifts that already have time punches stay. This cannot be undone from the screen (removed shifts are kept in the audit record).')) return;
    setBusy(true);
    try {
      const r = await axios.post(API + '/api/scheduler/clear-week', { facility_id: facilityId, kind, monday });
      alert('Cleared ' + r.data.removed + ' shift' + (r.data.removed === 1 ? '' : 's') + '.' + (r.data.kept ? ' ' + r.data.kept + ' left in place because they already have time punches.' : ''));
      await load();
    } catch (e) { alert('Clear failed: ' + ((e.response && e.response.data && e.response.data.error) || e.message)); }
    setBusy(false);
  };

  const openCoverage = async (a) => {
    setCoverFor(a); setCoverOpts(null); setCoverErr('');
    try {
      const r = await axios.get(API + '/api/scheduler/coverage-options',
        { params: { facility_id: a.facility_id, work_date: dOnly(a.work_date), paid_hours: a.paid_hours, exclude_user_id: a.user_id || undefined, staff_type: kind } });
      setCoverOpts(r.data.options || []);
    } catch (e) { setCoverErr((e.response && e.response.data && e.response.data.error) || 'Could not load options'); }
  };
  const callOut = async (a, kind2) => {
    setBusy(true);
    try {
      await axios.post(API + '/api/scheduler/assignments/' + a.id + '/callout', { kind: kind2 });
      setEditCell(null);
      await load();
      openCoverage(a);
    } catch (e) { alert('Could not record: ' + ((e.response && e.response.data && e.response.data.error) || e.message)); }
    setBusy(false);
  };
  const assignCover = async (opt) => {
    if (opt.seventh_consecutive_day || opt.added_doubletime > 0) {
      const parts = [];
      if (opt.added_doubletime > 0) parts.push(opt.added_doubletime + 'h at DOUBLE TIME');
      if (opt.added_overtime > 0) parts.push(opt.added_overtime + 'h at 1.5x');
      if (opt.seventh_consecutive_day) parts.push('7th consecutive day');
      if (!window.confirm('This pick costs: ' + parts.join(', ') + '. Assign anyway?')) return;
    }
    setBusy(true); setCoverErr('');
    try {
      await axios.post(API + '/api/scheduler/assignments/' + coverFor.id + '/cover',
        { user_id: opt.user_id, cost_acknowledged: { ot: opt.added_overtime, dt: opt.added_doubletime, seventh: opt.seventh_consecutive_day } });
      setCoverFor(null); setCoverOpts(null); await load();
    } catch (e) { setCoverErr((e.response && e.response.data && e.response.data.error) || 'Could not assign'); }
    setBusy(false);
  };
  const confirmDay = async () => {
    const d = addDays(new Date().toISOString().slice(0, 10), -1);
    if (!window.confirm('Confirm all of yesterday (' + fmtDate(d) + ') as worked? Callouts and no-shows you already marked stay as they are.')) return;
    setBusy(true);
    try {
      const r = await axios.post(API + '/api/scheduler/confirm-day', { facility_id: facilityId, work_date: d });
      alert('Confirmed ' + r.data.confirmed + ' shifts as worked.');
      await load();
    } catch (e) { alert('Could not confirm: ' + ((e.response && e.response.data && e.response.data.error) || e.message)); }
    setBusy(false);
  };

  if (err) return <div style={{ ...card, padding:16, color:T.blocking, fontWeight:600 }}>{err}</div>;
  if (!data) return <div style={{ color:T.inkSoft, fontSize:13, padding:12 }}>Loading schedule...</div>;

  const dates = [...Array(7)].map((_, i) => addDays(data.monday, i));
  const t5 = (t) => t ? String(t).slice(0, 5) : '';
  // Cell match: template_id first (a shift added from a cell keeps its row even with custom
  // times - Dimitra 1415633e), start_time as the fallback for shifts born without a template.
  const asgAt = (houseId, date, startTime, templateId) => data.assignments.filter(a =>
    String(a.house_id || '') === String(houseId || '') && dOnly(a.work_date) === date &&
    (templateId && a.template_id ? String(a.template_id) === String(templateId) : a.start_time === startTime));
  // Shifts in this house/day that no template row claims - rendered on the Other row so nothing is invisible.
  const otherAt = (houseId, date, tmpls) => data.assignments.filter(a =>
    String(a.house_id || '') === String(houseId || '') && dOnly(a.work_date) === date &&
    !tmpls.some(t => (t.id && a.template_id ? String(a.template_id) === String(t.id) : a.start_time === t.start_time)));
  const staffName = (sid) => {
    const s = data.staff.find(x => x.id === sid);
    if (s) return s.name;
    const a = data.assignments.find(x => x.staff_id === sid && x.staff_name);
    return a ? a.staff_name : '?';
  };

  // ── BHT: house sections, template rows x 7 days. Driver/clinical: person rows x 7 days.
  const houseGroups = {};
  if (kind === 'bht') {
    data.templates.forEach(t => { (houseGroups[t.house_name || 'Facility'] = houseGroups[t.house_name || 'Facility'] || []).push(t); });
  }

  const cellStyle = { border:'1px solid '+T.b100, padding:'4px 6px', minWidth:96, verticalAlign:'top', fontSize:12 };

  const renderAsg = (a) => (
    <div key={a.id} onClick={(e) => { e.stopPropagation(); setEditCell({ assignment:a }); }}
      style={{ cursor:'pointer', borderRadius:6, padding:'2px 6px', marginBottom:2,
        background: (a.status === 'out' || a.status === 'no_show') ? T.blockingBg : a.status === 'coverage' ? '#DCFCE7' : a.status === 'trainee' ? '#DBEAFE' : T.surfaceSoft || '#F8FAFC', /* 10/15 Cyndie: light green = coverage */
        border:'1px solid '+(a.status === 'out' ? '#fca5a5' : a.status === 'coverage' ? '#86efac' : a.status === 'trainee' ? '#93C5FD' : T.b100),
        textDecoration: a.status === 'cancelled' ? 'line-through' : 'none',
        color: a.status === 'out' ? '#7f1d1d' : T.ink }}>
      {xBtn(a)}<span style={{ fontWeight:600 }}>{a.staff_name}</span>
      {a.note && <span style={{ marginLeft:4, fontSize:10.5, color:'#B45309', fontStyle:'italic' }}>{'\u2014 ' + a.note}</span>} {/* 10/15 Cyndie a8514cbe: note beside the name */}
      {a.status === 'out' && <span style={{ marginLeft:4, fontSize:10.5, fontWeight:700 }}>OUT</span>}
      {a.status === 'coverage' && <span style={{ marginLeft:4, fontSize:10.5, color:'#166534', fontWeight:700 }}>COV</span>}
      {a.status === 'trainee' && <span style={{ marginLeft:4, fontSize:10.5, color:'#1D4ED8', fontWeight:700 }}>TRAINEE</span>} {/* 10/24 Cyndie 5f3083ba: light blue = trainee */}
      {a.status === 'no_show' && <span style={{ marginLeft:4, fontSize:10.5, fontWeight:700 }}>NO SHOW</span>}
      {a.status === 'on_call' && <span style={{ marginLeft:4, fontSize:10.5, color:'#5D6B7A', fontWeight:700 }}>ON CALL</span>}
      {(kind !== 'bht' || a._showTime) && <div style={{ fontSize:11, color:T.inkSoft }}>{fmtTime(a.start_time)}-{fmtTime(a.end_time)}</div>}
    </div>
  );

  const bhtGrid = Object.keys(houseGroups).map(houseName => (
    <div key={houseName} style={{ ...card, marginBottom:14, overflow:'hidden' }}>
      <div style={{ padding:'9px 14px', background:T.b100, fontWeight:600, fontSize:14 }}>{houseName}</div>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>
          <th style={{ ...cellStyle, ...label, width:110 }}>Shift</th>
          {dates.map((d, i) => <th key={d} style={{ ...cellStyle, ...label }}>{DOW[i]} {fmtDate(d)}</th>)}
        </tr></thead>
        <tbody>
          {houseGroups[houseName].map(t => (
            <tr key={t.id}>
              <td style={{ ...cellStyle, fontWeight:600, whiteSpace:'nowrap' }}>{t.label}</td>
              {dates.map(d => {
                const here = asgAt(t.house_id, d, t.start_time, t.id)
                  .map(x => ({ ...x, _showTime: t5(x.start_time) !== t5(t.start_time) || t5(x.end_time) !== t5(t.end_time) }));
                const activeHere = here.filter(x => !['cancelled', 'out', 'no_show'].includes(x.status));
                const need = (t.heads_needed === 0 || t.heads_needed === '0') ? 0 : (t.heads_needed || 1);
                const short = activeHere.length < need;
                return (
                  <td key={d} style={{ ...cellStyle, background: activeHere.length === 0 ? '#FEF9C3' : short ? '#FFF7E6' : undefined }}> {/* 10/15: light yellow = open shift */}
                    {here.map(renderAsg)}
                    {short && <div style={{ fontSize:10, fontWeight:700, color: activeHere.length === 0 ? '#E5484D' : '#B45309', marginBottom:2 }}>{activeHere.length}/{need} filled</div>}
                    {canEdit && <div onClick={() => setEditCell({ template:t, date:d })}
                      style={{ color:'#0878E8', fontSize:11, fontWeight:600, cursor:'pointer' }}>+ Add</div>}
                  </td>
                );
              })}
            </tr>
          ))}
          {(() => {
            const tmpls = houseGroups[houseName];
            const hid = tmpls[0] ? tmpls[0].house_id : null;
            const perDay = dates.map(d => otherAt(hid, d, tmpls).map(x => ({ ...x, _showTime: true })));
            if (!canEdit && !perDay.some(x => x.length)) return null;
            return (
              <tr key="other">
                <td style={{ ...cellStyle, fontWeight:600, whiteSpace:'nowrap', color:T.inkSoft }}>Other</td>
                {dates.map((d, i) => (
                  <td key={d} style={cellStyle}>
                    {perDay[i].map(renderAsg)}
                    {canEdit && <div onClick={() => setEditCell({ house:{ house_id: hid, house_name: houseName }, date:d })}
                      style={{ color:'#0878E8', fontSize:11, fontWeight:600, cursor:'pointer' }}>+ Add</div>}
                  </td>
                ))}
              </tr>
            );
          })()}
        </tbody>
      </table>
    </div>
  ));

  const personGrid = (
    <div style={{ ...card, overflow:'hidden', marginBottom:14 }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>
          <th style={{ ...cellStyle, ...label, width:140 }}>{kind === 'driver' ? 'Driver' : 'Clinician'}</th>
          {dates.map((d, i) => <th key={d} style={{ ...cellStyle, ...label }}>{DOW[i]} {fmtDate(d)}</th>)}
        </tr></thead>
        <tbody>
          {data.staff.map(s => (
            <tr key={s.id}>
              <td style={{ ...cellStyle, fontWeight:600 }}>{s.name}
                {kind === 'clinical' && <div style={{ fontSize:10.5, color:T.inkSoft }}>{s.staff_type === 'therapist' ? 'Therapist' : 'Case Mgr'}</div>}
              </td>
              {dates.map(d => {
                const here = data.assignments.filter(a => a.staff_id === s.id && dOnly(a.work_date) === d);
                return (
                  <td key={d} style={{ ...cellStyle, cursor:'pointer' }}
                    onClick={() => setEditCell({ person:s, date:d })}>
                    {here.map(renderAsg)}
                    {here.length === 0 && <span style={{ color:T.textMuted || '#8291A1' }}>+</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const kindStaffIds = new Set(data.staff.map(s => s.id));
  const bhtStaffIds = [...new Set([...data.staff.map(s => s.id),
    ...data.assignments.filter(a => kindStaffIds.has(a.staff_id)).map(a => a.staff_id)])];
  const bhtStaffGrid = (
    <div style={{ ...card, overflow:'hidden', marginBottom:14 }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>
          <th style={{ ...cellStyle, ...label, width:140 }}>Staff</th>
          {dates.map((d, i) => <th key={d} style={{ ...cellStyle, ...label }}>{DOW[i]} {fmtDate(d)}</th>)}
          <th style={{ ...cellStyle, ...label, width:110 }}>Week</th>
        </tr></thead>
        <tbody>
          {bhtStaffIds.map(sid => {
            const mine = data.assignments.filter(a => a.staff_id === sid);
            const h = data.hours[sid];
            return (
            <tr key={sid}>
              <td style={{ ...cellStyle, fontWeight:600 }}>{staffName(sid)}</td>
              {dates.map(d => {
                const here = mine.filter(a => dOnly(a.work_date) === d);
                return (
                  <td key={d} style={{ ...cellStyle, cursor: canEdit ? 'pointer' : 'default' }}
                    onClick={() => { if (canEdit && here.length === 0) setEditCell({ person: { id: sid }, date: d }); }}>
                    {here.map(a => (
                      <div key={a.id} onClick={() => setEditCell({ assignment:a })}
                        style={{ cursor:'pointer', fontSize:11.5, marginBottom:2,
                          textDecoration: a.status === 'cancelled' ? 'line-through' : 'none' }}>
                        {xBtn(a)}<span style={{ fontWeight:600 }}>{a.house_name || ''}</span> {fmtTime(a.start_time)}-{fmtTime(a.end_time)}
                        {a.status === 'out' && <span style={{ color:'#E5484D', fontWeight:700 }}> OUT</span>}
                        {a.status === 'no_show' && <span style={{ color:'#E5484D', fontWeight:700 }}> NS</span>}
                        {a.status === 'coverage' && <span style={{ color:'#166534', fontWeight:700 }}> COV</span>}
                      </div>
                    ))}
                  </td>
                );
              })}
              <td style={{ ...cellStyle, fontSize:12 }}>
                {h ? <div>
                  <div style={{ fontWeight:600 }}>{h.total_hours}h</div>
                  {h.overtime > 0 && <div style={{ color:'#B45309', fontWeight:600 }}>{h.overtime}h OT</div>}
                  {h.doubletime > 0 && <div style={{ color:'#E5484D', fontWeight:700 }}>{h.doubletime}h 2x</div>}
                </div> : <span style={{ color:'#8291A1' }}>0h</span>}
              </td>
            </tr>);
          })}
        </tbody>
      </table>
    </div>
  );

  // 10/15 Cyndie 5453b7d0: a day off = a day this week with no shift (cancelled shifts do not count as working)
  const offDaysFor = (sid) => dates.filter(d => !data.assignments.some(a => String(a.staff_id) === String(sid) && dOnly(a.work_date) === d && a.status !== 'cancelled')).map(d => DOW[dates.indexOf(d)] || '');
  const notesFor = (sid) => data.assignments.filter(a => String(a.staff_id) === String(sid) && a.note && a.status !== 'cancelled')
    .sort((x, y) => String(x.work_date).localeCompare(String(y.work_date)))
    .map(a => (DOW[dates.indexOf(dOnly(a.work_date))] || '') + ': ' + a.note);
  const hoursSidebar = (
    <div style={{ ...card, padding:12, minWidth:230, maxHeight:520, overflowY:'auto' }}>
      <div style={{ ...label, marginBottom:8 }}>Week Hours (CA)</div>
      {Object.keys(data.hours).length === 0 && <div style={{ fontSize:12.5, color:T.inkSoft }}>No shifts scheduled yet.</div>}
      {Object.entries(data.hours)
        .sort((a, b) => (b[1].overtime + b[1].doubletime) - (a[1].overtime + a[1].doubletime))
        .map(([sid, h]) => (
        <div key={sid} style={{ padding:'6px 0', borderTop:'1px solid '+T.b100, fontSize:12.5 }}>
          <div style={{ fontWeight:600 }}>{staffName(sid)}{offDaysFor(sid).length > 0 && <span style={{ marginLeft:6, fontSize:11, fontWeight:500, color:T.inkSoft }}>off {offDaysFor(sid).join(', ')}</span>}</div>
          <div style={{ color:T.inkSoft }}>
            {h.total_hours}h total
            {h.overtime > 0 && <span style={{ color:T.warning, fontWeight:600 }}> · {h.overtime}h OT</span>}
            {h.doubletime > 0 && <span style={{ color:T.blocking, fontWeight:700 }}> · {h.doubletime}h 2x</span>}
          </div>
          {notesFor(sid).map((n, i) => <div key={i} style={{ fontSize:11, color:'#B45309', fontStyle:'italic' }}>{n}</div>)}
        </div>
      ))}
      {(data.staff || []).filter(s => data.hours[s.id] === undefined && data.hours[String(s.id)] === undefined).map(s => (
        <div key={'off-' + s.id} style={{ padding:'6px 0', borderTop:'1px solid '+T.b100, fontSize:12.5 }}>
          <div style={{ fontWeight:600 }}>{s.name}<span style={{ marginLeft:6, fontSize:11, fontWeight:500, color:T.inkSoft }}>off all week</span></div>
        </div>
      ))}
    </div>
  );

  const covBanner = data.needs_coverage.length > 0 && (
    <div style={{ ...card, background:T.blockingBg, border:'1px solid #fca5a5', padding:'10px 14px', marginBottom:14 }}>
      <div style={{ fontWeight:700, color:'#7f1d1d', fontSize:13, marginBottom:4 }}>
        NEEDS COVERAGE — {data.needs_coverage.length} open slot{data.needs_coverage.length === 1 ? '' : 's'}
      </div>
      {data.needs_coverage.map((c, i) => (
        <div key={i} style={{ fontSize:12.5, color:'#7f1d1d' }}>
          {DOW[(new Date(c.work_date + 'T00:00:00').getDay() + 6) % 7]} {fmtDate(c.work_date)} · {c.house_name || 'Facility'} · {fmtTime(c.start_time)}-{fmtTime(c.end_time)} (was {c.was})
        </div>
      ))}
    </div>
  );

  const hm = (t) => t ? String(t).slice(0, 5) : t;  // 06:00:00 -> 06:00
  const ec = editCell;
  const editPanel = ec && (() => {
    const a = ec.assignment;
    const init = a || {};
    const tmpl = ec.template;
    return (
      <div onClick={() => setEditCell(null)}
        style={{ position:'fixed', inset:0, background:'rgba(7,26,45,0.45)', zIndex:300,
          display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div onClick={e => e.stopPropagation()}
          style={{ ...card, padding:0, width:380, overflow:'hidden', borderRadius:11,
            boxShadow:'0 12px 40px rgba(7,26,45,0.25)' }}>
          <div style={{ padding:'13px 18px', background:T.b100, borderBottom:'1px solid '+T.b200,
            fontWeight:600, fontSize:14 }}>
            {a ? 'Edit shift' : 'Add shift'} — {fmtDate(a ? a.work_date : ec.date)}
            {tmpl ? ' · ' + (tmpl.house_name || '') + ' ' + tmpl.label : (ec.house ? ' · ' + ec.house.house_name + ' (custom)' : '')}
          </div>
          {ec.conflict && (
            <div style={{ padding:'14px 18px', background:'#FFF7E6', borderBottom:'1px solid #F59E0B' }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#22303E', marginBottom:4 }}>
                {(ec.conflict.staff_name || 'That person') + ' is already on ' + (ec.conflict.house_name || 'another shift') + ' ' + fmtTime(ec.conflict.start_time) + '-' + fmtTime(ec.conflict.end_time) + ' that day.'}
              </div>
              <div style={{ fontSize:12.5, color:'#5D6B7A', marginBottom:10 }}>
                {ec.conflict.can_swap && ec.conflict.existing_id ? 'Swap the two people, or move this person here and leave their other shift open.' : 'Move this person here and leave their other shift open?'}
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {ec.conflict.can_swap && ec.conflict.existing_id &&
                  <button type="button" onClick={() => resolveConflict('swap')} style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0878E8', color:'#fff', fontSize:12.5, fontWeight:500, cursor:'pointer' }}>Swap the two</button>}
                <button type="button" onClick={() => resolveConflict('move')} style={{ padding:'7px 12px', borderRadius:8, border:'1px solid #0878E8', background:'#fff', color:'#0878E8', fontSize:12.5, fontWeight:500, cursor:'pointer' }}>
                  {'Move ' + String(ec.conflict.staff_name || 'them').split(' ')[0] + ' here'}</button>
                <button type="button" onClick={() => setEditCell({ ...ec, conflict: null })} style={{ padding:'7px 12px', borderRadius:8, border:'1px solid #D8E3EE', background:'#fff', color:'#5D6B7A', fontSize:12.5, fontWeight:500, cursor:'pointer' }}>Cancel</button>
              </div>
            </div>
          )}
          <form style={{ padding:18 }} onSubmit={e => {
            e.preventDefault();
            const f = e.target;
            const payload = {
              staff_id: f.staff.value,
              facility_id: facilityId,
              house_id: tmpl ? tmpl.house_id : (a ? a.house_id : (ec.house ? ec.house.house_id : null)),
              template_id: tmpl ? tmpl.id : (a ? a.template_id : null),
              work_date: a ? a.work_date : ec.date,
              start_time: f.start.value, end_time: f.end.value,
              paid_hours: Number(f.hours.value),
              status: f.status.value, note: f.note.value || null,
            };
            save(payload, a ? a.id : null);
          }}>
            <div style={{ display:'grid', gap:12 }}>
              <div>
                <div style={{ fontSize:10, letterSpacing:'1px', textTransform:'uppercase', color:'#8291A1', fontWeight:600, marginBottom:4 }}>Staff member</div>
                <select name="staff" style={{ ...inp, width:'100%' }} defaultValue={init.staff_id || (ec.person ? ec.person.id : '')} required>
                  <option value="">Select…</option>
                  {(((tmpl || (a && (data.templates || []).find(t => String(t.id) === String(a.template_id)))) || {}).is_on_call ? (data.staff_any || data.staff) : data.staff).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:10, letterSpacing:'1px', textTransform:'uppercase', color:'#8291A1', fontWeight:600, marginBottom:4 }}>Start / End / Paid hours</div>
                <div style={{ display:'flex', gap:8 }}>
                  <input name="start" type="time" style={{ ...inp, flex:1 }} defaultValue={hm(init.start_time) || hm(tmpl ? tmpl.start_time : '08:00')} required />
                  <input name="end" type="time" style={{ ...inp, flex:1 }} defaultValue={hm(init.end_time) || hm(tmpl ? tmpl.end_time : '17:00')} required />
                  <input name="hours" style={{ ...inp, width:64 }} defaultValue={init.paid_hours || (tmpl ? tmpl.paid_hours : 8)} required />
                </div>
              </div>
              <div>
                <div style={{ fontSize:10, letterSpacing:'1px', textTransform:'uppercase', color:'#8291A1', fontWeight:600, marginBottom:4 }}>Status</div>
                <select name="status" style={{ ...inp, width:'100%' }} defaultValue={init.status || 'scheduled'}>
                  <option value="scheduled">Scheduled</option>
                  <option value="on_call">On call</option>
                  <option value="worked">Worked</option>
                  <option value="out">Out (sick/no-show)</option>
                  <option value="coverage">Coverage</option>
                  <option value="trainee">Trainee</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <input name="note" style={inp} defaultValue={init.note || ''} placeholder="Note beside the name: hours, OT, shift change (optional)" />
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {a && canEdit && ['scheduled', 'coverage', 'on_call', 'trainee'].includes(a.status) &&
                  <button type="button" style={{ ...btn(false), color:'#B45309' }} disabled={busy} onClick={() => callOut(a, 'out')}>Called out</button>}
                {a && canEdit && ['scheduled', 'coverage', 'on_call', 'trainee'].includes(a.status) &&
                  <button type="button" style={{ ...btn(false), color:'#E5484D' }} disabled={busy} onClick={() => callOut(a, 'no_show')}>No-show</button>}
                <span style={{ flex:1 }} />
                <button type="button" style={btn(false)} onClick={() => setEditCell(null)}>Close</button>
                <button type="submit" style={btn(true)} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  })();

  const coverPanel = !coverFor ? null : (
    <div style={{ position:'fixed', inset:0, background:'rgba(7,26,45,0.45)', zIndex:70,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={() => { setCoverFor(null); load(); }}>
      <div style={{ ...card, width:560, maxHeight:'82vh', overflowY:'auto', padding:18 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:2 }}>Find coverage</div>
        <div style={{ fontSize:12.5, color:T.inkSoft, marginBottom:10 }}>
          {fmtDate(coverFor.work_date)}{coverFor.house_name ? ' at ' + coverFor.house_name : ''} {fmtTime(coverFor.start_time)}-{fmtTime(coverFor.end_time)} ({coverFor.paid_hours}h) ranked by real cost under CA overtime law</div>
        {coverErr && <div style={{ fontSize:12.5, color:'#E5484D', marginBottom:8 }}>{coverErr}</div>}
        {!coverOpts ? <div style={{ fontSize:13, color:T.inkSoft }}>Checking hours for everyone this week...</div> : (
          <div style={{ display:'grid', gap:6 }}>
            {coverOpts.map(o => (
              <div key={o.user_id} style={{ display:'flex', alignItems:'center', gap:10,
                padding:'8px 10px', border:'1px solid ' + T.b200, borderRadius:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600 }}>{o.name}
                    {o.already_scheduled_that_day && <span style={{ marginLeft:6, fontSize:10.5, color:'#B45309', fontWeight:700 }}>ALREADY ON THAT DAY</span>}
                    {o.seventh_consecutive_day && <span style={{ marginLeft:6, fontSize:10.5, color:'#E5484D', fontWeight:700 }}>7TH DAY IN A ROW</span>}
                  </div>
                  <div style={{ fontSize:11.5, color:T.inkSoft }}>
                    {o.added_regular > 0 && <span>{o.added_regular}h straight time </span>}
                    {o.added_overtime > 0 && <span style={{ color:'#B45309', fontWeight:600 }}>{o.added_overtime}h at 1.5x </span>}
                    {o.added_doubletime > 0 && <span style={{ color:'#E5484D', fontWeight:700 }}>{o.added_doubletime}h at 2x </span>}
                    <span>{'week lands at ' + o.week_total_after + 'h'}</span>
                  </div>
                </div>
                <button style={btn(o.added_overtime === 0 && o.added_doubletime === 0)} disabled={busy}
                  onClick={() => assignCover(o)}>Assign</button>
              </div>
            ))}
            {coverOpts.length === 0 && <div style={{ fontSize:13, color:T.inkSoft }}>No schedulable staff at this facility.</div>}
          </div>
        )}
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:12 }}>
          <button style={btn(false)} onClick={() => { setCoverFor(null); load(); }}>Close</button>
        </div>
      </div>
    </div>
  );

  const fixesPanel = !fixesOpen ? null : (
    <div onClick={() => setFixesOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(10,34,57,0.45)', zIndex:80,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width:560, maxWidth:'94vw', maxHeight:'80vh', overflowY:'auto', padding:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <b style={{ fontSize:15, color:T.ink }}>Time fixes {'\u2014'} pending requests</b>
          <button style={btn(false)} onClick={() => setFixesOpen(false)}>Done</button>
        </div>
        {!fixRows.length && <div style={{ fontSize:13, color:T.inkSoft, padding:'10px 0' }}>No pending time corrections at this facility.</div>}
        {fixRows.map(f => {
          const d = f.details || {};
          const kindTxt = d.kind === 'missed_in' ? 'Forgot to clock in' : d.kind === 'missed_out' ? 'Forgot to clock out' : 'Wrong punch time';
          return (
            <div key={f.id} style={{ borderTop:'1px solid '+T.b200, padding:'10px 0', fontSize:13 }}>
              <div style={{ fontWeight:600, color:T.ink }}>{f.person || '?'} {'\u00B7'} {f.work_date} {'\u00B7'} {fmtTime(f.start_time)}{'\u2013'}{fmtTime(f.end_time)}{f.house_name ? ' \u00B7 ' + f.house_name : ''}</div>
              <div style={{ color:T.inkSoft, marginTop:3 }}>{kindTxt} {'\u2192'} <b style={{ color:T.ink }}>{d.proposed_time ? new Date(d.proposed_time).toLocaleString('en-US', { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }) : '?'}</b></div>
              <div style={{ color:T.inkSoft, marginTop:2 }}>Reason: {d.reason || '\u2014'}</div>
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <button style={btn(true)} disabled={fixActBusy === f.id} onClick={() => resolveFix(f.id, true)}>{fixActBusy === f.id ? 'Working\u2026' : 'Approve \u2014 write punch'}</button>
                <button style={btn(false)} disabled={fixActBusy === f.id} onClick={() => resolveFix(f.id, false)}>Deny</button>
              </div>
            </div>);
        })}
        <div style={{ fontSize:11.5, color:T.inkSoft, marginTop:10 }}>Approving writes the corrected punch attributed to you and the requester. Original punches are never edited.</div>
      </div>
    </div>);

  const managePanel = !manageOpen ? null : (
    <div style={{ position:'fixed', inset:0, background:'rgba(7,26,45,0.45)', zIndex:60,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={() => { setManageOpen(false); load(); }}>
      <div style={{ ...card, width:480, maxHeight:'80vh', overflowY:'auto', padding:18 }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', marginBottom:10 }}>
          <div style={{ fontSize:15, fontWeight:600 }}>Manage schedulable staff</div>
          <span style={{ flex:1 }} />
          <button style={btn(false)} onClick={() => { setManageOpen(false); load(); }}>Done</button>
        </div>
        <div style={{ fontSize:12, color:T.inkSoft, marginBottom:12 }}>
          People with facility access. Toggle Schedulable to make them available on the schedule grid.
        </div>
        {manageErr && <div style={{ fontSize:12.5, color:'#E5484D', marginBottom:8 }}>{manageErr}</div>}
        {!manageRows ? <div style={{ fontSize:13, color:T.inkSoft }}>Loading…</div> : (
          <div style={{ display:'grid', gap:6 }}>
            {manageRows.map(r => (
              <div key={r.user_id} style={{ display:'flex', alignItems:'center', gap:10,
                padding:'7px 10px', border:'1px solid ' + T.b200, borderRadius:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:500 }}>{r.first_name} {r.last_name}</div>
                  <div style={{ fontSize:11, color:T.inkSoft }}>{r.role}{r.staff_type ? ' \u00b7 ' + r.staff_type : ''}</div>
                </div>
                <button style={btn(r.schedulable)} onClick={() => toggleSchedulable(r)}>
                  {r.schedulable ? 'Schedulable' : 'Not schedulable'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const groupsPanel = grp && (grp.enabled || grp.preview) && (
    <div style={{ ...card, padding:14, marginTop:14 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:10 }}>
        <div style={{ fontWeight:600, fontSize:14 }}>Group schedule</div>
        <span style={{ fontSize:12, color:T.inkSoft }}>From the EMR group schedule. Edit it there and it changes here.</span>
        {!grp.enabled && <span style={{ fontSize:11.5, fontWeight:600, color:'#B45309', background:'#FFF7E6', padding:'2px 8px', borderRadius:6 }}>Preview - staff cannot see this yet</span>}
        <span style={{ flex:1 }} />
        {grp.can_toggle && <button style={btn(false)} onClick={toggleGroups}>{grp.enabled ? 'Hide from staff' : 'Show to staff'}</button>}
      </div>
      {(grp.groups || []).length === 0 ? (
        <div style={{ fontSize:12.5, color:T.inkSoft }}>No groups on the schedule for this week.</div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7, minmax(130px, 1fr))', gap:8, minWidth:940 }}>
            {[0, 1, 2, 3, 4, 5, 6].map(i => {
              const d = addDays(grp.monday, i);
              const list = (grp.groups || []).filter(x => x.work_date === d);
              return (
                <div key={d}>
                  <div style={{ ...label, marginBottom:5 }}>{DOW[i]} {fmtDate(d)}</div>
                  {list.length === 0 ? <div style={{ fontSize:12, color:'#8291A1' }}>-</div> : list.map((x, j) => (
                    <div key={j} style={{ fontSize:12, padding:'6px 8px', marginBottom:5, border:'1px solid ' + T.b200, borderRadius:7, background:'#fff',
                      textDecoration: x.status === 'cancelled' ? 'line-through' : 'none' }}>
                      <div style={{ fontWeight:600 }}>{fmtTime(x.start_time)} {x.topic || 'Group'}</div>
                      <div style={{ color:T.inkSoft }}>{[x.program, x.house_name, x.facilitator_name, x.duration_minutes ? x.duration_minutes + ' min' : null].filter(Boolean).join(' - ')}</div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding:20, fontFamily:"'Inter', system-ui, sans-serif", color:T.ink }}>
      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:14, flexWrap:'wrap' }}>
        <div style={{ fontSize:20, fontWeight:600 }}>Scheduler</div>
        <span style={{ display:'inline-flex', gap:4 }}>
          {[['bht','BHT'],['driver','Drivers'],['clinical','Clinical']].map(([k, l]) => (
            <button key={k} style={btn(kind === k)} onClick={() => setKind(k)}>{l}</button>
          ))}
        </span>
        {kind === 'bht' && <span style={{ display:'inline-flex', gap:4, marginLeft:6 }}>
          <button style={btn(view === 'house')} onClick={() => setView('house')}>Houses</button>
          <button style={btn(view === 'staff')} onClick={() => setView('staff')}>Staff</button>
        </span>}
        <span style={{ flex:1 }} />
        <select value={facilityId} onChange={e => setFacilityId(e.target.value)}
          style={{ padding:'7px 10px', borderRadius:8, border:'1px solid #CBD5E1', fontSize:13, fontFamily:'inherit', marginRight:8 }}>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
        </select>
        <button style={btn(false)} onClick={() => setMonday(addDays(monday, -7))}>{'\u2039'} Prev</button>
        <strong style={{ fontSize:13 }}>{fmtDate(data.monday)} – {fmtDate(data.sunday)}</strong>
        <button style={btn(false)} onClick={() => setMonday(addDays(monday, 7))}>Next {'\u203A'}</button>
        {canEdit && <button style={btn(false)} onClick={copyWeek} disabled={busy}>Copy last week</button>}
        {canEdit && String(monday) > new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }) && <button disabled={busy} title="Remove every shift on this week's grid (future weeks only)" style={{ ...btn(false), color: '#b42318', borderColor: '#f3c2bd' }} onClick={clearWeek}>Clear week</button>}
        {canEdit && <button style={btn(false)} onClick={confirmDay} disabled={busy}>Confirm yesterday</button>}
        {canEdit && <button style={btn(false)} onClick={() => { setManageOpen(true); loadManage(); }}>Manage staff</button>}
        {canEdit && <button style={btn(fixRows.length > 0)} onClick={() => { setFixesOpen(true); loadFixes(); }}>Time fixes{fixRows.length ? ' (' + fixRows.length + ')' : ''}</button>}
        {fixesPanel}
      </div>
      {covBanner}
      <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
        <div style={{ flex:1 }}>{kind === 'bht' ? (view === 'staff' ? bhtStaffGrid : bhtGrid) : personGrid}</div>
        {hoursSidebar}
      </div>
      {groupsPanel}
      {editPanel}
      {managePanel}
      {coverPanel}
    </div>
  );
}
